import {
  Alert,
  ComboBox,
  Form,
  InnerForm,
  InputTags,
  Link,
  Modal,
  Text,
} from "@fluidattacks/design";
import type { IItem, IUseModal } from "@fluidattacks/design";
import capitalize from "lodash/capitalize";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useAddVulnerabilityPolicy } from "./hooks";
import { validations } from "./validations";

import type { IVulnerabilityPoliciesData } from "../../types";

const prefix = "organization.tabs.policies.findings.";

interface IModalProps {
  data: IVulnerabilityPoliciesData[];
  modalRef: IUseModal;
  organizationId: string;
  vulnerabilityTreatment: string;
}

const CI_URL =
  "https://help.fluidattacks.com/portal/en/kb/articles/prevent-the-deployment-of-builds-with-vulnerabilities";

const AddVulnerabilityTypeModal = ({
  data,
  modalRef,
  organizationId,
  vulnerabilityTreatment,
}: IModalProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { handleAddPolicy, loading, suggestions } = useAddVulnerabilityPolicy(
    modalRef,
    organizationId,
    vulnerabilityTreatment,
  );
  const isAccepted = vulnerabilityTreatment === "ACCEPTED";
  const activePolicies = data.map((policy): Record<string, string> => {
    return { name: policy.name, status: policy.status };
  });
  const disabledKeys = activePolicies.flatMap((policy): string =>
    policy.name.startsWith("All") ? "All" : policy.name,
  );
  const treatment =
    vulnerabilityTreatment === "ACCEPTED" ? "temporary" : "permanent";

  return (
    <Modal
      modalRef={modalRef}
      size={"md"}
      title={t(`${prefix}modal.${treatment}.title`)}
    >
      <Form
        cancelButton={{ onClick: modalRef.close }}
        confirmButton={{ disabled: loading, label: t(`${prefix}modal.button`) }}
        defaultValues={{ tags: "", type: "" }}
        id={"add-vulnerability-type"}
        onSubmit={handleAddPolicy}
        yupSchema={validations(isAccepted, suggestions)}
      >
        <InnerForm>
          {({ control, ...methods }): JSX.Element => {
            const options = methods.watch("typeOptions");
            const optionsArray =
              typeof options === "string"
                ? [options]
                : (options as string[] | undefined);
            const types = isAccepted ? optionsArray : [];

            return (
              <Fragment>
                <ComboBox
                  control={control}
                  disabledKeys={disabledKeys}
                  helpText={t(`${prefix}modal.type.help`)}
                  items={suggestions.map((item): IItem => {
                    return {
                      name: item,
                      tag:
                        disabledKeys.includes(item) ||
                        disabledKeys.some((key): boolean => item.includes(key))
                          ? t(`${prefix}modal.tag`, {
                              status: activePolicies
                                .filter(({ name }): boolean => name === item)
                                .map(({ status }): string =>
                                  status === "SUBMITTED"
                                    ? "Requested"
                                    : capitalize(status),
                                )
                                .join(),
                            })
                          : undefined,
                      value: item.startsWith("All") ? "All" : item,
                    };
                  })}
                  label={t(`${prefix}modal.type.label`)}
                  multiSelection={isAccepted}
                  name={"type"}
                  placeholder={t(`${prefix}modal.type.placeholder`)}
                  required={true}
                  {...methods}
                />
                <InputTags
                  control={control}
                  helpText={t(`${prefix}modal.tags.help`)}
                  label={t(`${prefix}modal.tags.label`)}
                  name={"tags"}
                />
                <Alert variant={"warning"}>
                  {t(`${prefix}modal.alert.start`)}
                  <Link
                    color={theme.palette.warning[700]}
                    href={CI_URL}
                    iconPosition={"hidden"}
                  >
                    {<b>{t(`${prefix}modal.alert.link`)}</b>}
                  </Link>
                  {t(`${prefix}modal.alert.end`)}
                </Alert>
                {isAccepted && (types?.length ?? 0) > 0 ? (
                  <Text color={theme.palette.gray[800]} mt={0.5} size={"md"}>
                    {t(`${prefix}modal.${treatment}.selected`)}
                    <b>
                      {t(`${prefix}modal.${treatment}.total`, {
                        total: types?.length,
                      })}
                    </b>
                  </Text>
                ) : undefined}
              </Fragment>
            );
          }}
        </InnerForm>
      </Form>
    </Modal>
  );
};

export { AddVulnerabilityTypeModal };
